import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Coins, Zap, Eye, EyeOff, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { register, login, saveToken, resendVerification } from '../api';

// ⚠ Google OAuth: update redirect URI di Google Console → https://dynaparse.com/api/v1/auth/google/callback
const GOOGLE_OAUTH_URL = 'https://dynaparse.com/api/v1/auth/google';
// ⚠ reCAPTCHA: update site key di Google reCAPTCHA Console untuk domain dynaparse.com
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

async function getRecaptchaToken(action) {
  try {
    if (!window.grecaptcha || !RECAPTCHA_SITE_KEY) return null;
    return await new Promise((resolve, reject) =>
      window.grecaptcha.ready(() =>
        window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve).catch(reject)
      )
    );
  } catch {
    return null;
  }
}

function GoogleIcon() {
  return (
    <svg viewBox='0 0 24 24' className='w-4 h-4 flex-shrink-0' xmlns='http://www.w3.org/2000/svg'>
      <path d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' fill='#4285F4' />
      <path d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' fill='#34A853' />
      <path d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z' fill='#FBBC05' />
      <path d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' fill='#EA4335' />
    </svg>
  );
}

export default function AuthModal({ open, onOpenChange, mode, onModeChange }) {
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [checkEmail, setCheckEmail] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // idle | loading | sent | error
  const navigate = useNavigate();

  const isSignup = mode === 'signup';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken(isSignup ? 'register' : 'login');
      const data = isSignup
        ? await register(form.email, form.password, recaptchaToken)
        : await login(form.email, form.password, recaptchaToken);

      if (data.message === 'check_email') {
        setPendingEmail(form.email);
        setCheckEmail(true);
        return;
      }

      saveToken(data.access_token);
      onOpenChange(false);
      toast.success(isSignup ? 'Account created! 10 bonus credits added.' : 'Welcome back!', {
        description: isSignup ? 'Redirecting to your dashboard...' : 'Loading your dashboard...',
      });
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('loading');
    try {
      await resendVerification(pendingEmail);
      setResendStatus('sent');
    } catch (_) {
      setResendStatus('error');
    }
  };

  const handleOpenChange = (val) => {
    if (!val) { setCheckEmail(false); setResendStatus('idle'); setForm({ email: '', password: '' }); }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md p-0 overflow-hidden'>
        {/* Top gradient strip */}
        <div className='h-1.5 bg-gradient-brand w-full' />

        <div className='px-8 pt-6 pb-8'>
          {/* Brand header */}
          <div className='flex items-center gap-2 mb-6'>
            <div className='w-8 h-8 rounded-lg bg-primary flex items-center justify-center'>
              <Zap className='w-4 h-4 text-primary-foreground' strokeWidth={2.2} />
            </div>
            <span className='font-heading font-bold text-base text-foreground'>
              Dyna<span className='text-primary'>Parse</span>
            </span>
          </div>

          {checkEmail ? (
            /* ─── Check-your-email screen ─── */
            <div className='text-center py-2'>
              <div className='w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4'>
                <Mail className='w-7 h-7 text-primary' />
              </div>
              <DialogTitle className='font-heading text-xl font-bold text-foreground mb-1'>Check your email</DialogTitle>
              <DialogDescription className='text-muted-foreground text-sm mb-3'>
                We sent a verification link to
              </DialogDescription>
              <p className='font-semibold text-foreground text-sm mb-3'>{pendingEmail}</p>
              <p className='text-xs text-muted-foreground mb-6'>
                Click the link to activate your account and get your 10 bonus credits.
                The link expires in 24 hours.
              </p>
              {resendStatus === 'sent' && (
                <p className='text-sm text-green-600 font-medium mb-3'>Email resent! Check your inbox.</p>
              )}
              {resendStatus === 'error' && (
                <p className='text-sm text-destructive mb-3'>Failed to resend. Please try again.</p>
              )}
              <Button variant='outline' size='sm' disabled={resendStatus === 'loading' || resendStatus === 'sent'}
                onClick={handleResend}
                className='gap-2'>
                <RefreshCw className='w-3.5 h-3.5' />
                {resendStatus === 'loading' ? 'Sending…' : 'Resend verification email'}
              </Button>
            </div>
          ) : (
          <>
          <DialogHeader className='mb-2'>
            <DialogTitle className='font-heading text-2xl font-bold text-foreground'>
              {isSignup ? 'Create your account' : 'Welcome back'}
            </DialogTitle>
            <DialogDescription className='text-muted-foreground text-sm'>
              {isSignup
                ? 'Get 10 bonus credits instantly — no credit card needed.'
                : 'Sign in to access your dashboard and API keys.'}
            </DialogDescription>
          </DialogHeader>

          {/* Credit badge (signup only) */}
          {isSignup && (
            <div className='flex items-center gap-2 bg-credit/10 border border-credit/25 rounded-xl px-3 py-2.5 mb-4 mt-4'>
              <Coins className='w-4 h-4 text-credit flex-shrink-0' />
              <p className='text-xs font-medium text-credit'>
                You&apos;ll receive <strong>10 bonus credits</strong> on sign-up.
              </p>
            </div>
          )}

          {/* Google OAuth button */}
          <a
            href={GOOGLE_OAUTH_URL}
            className='mt-4 flex items-center justify-center gap-2.5 w-full h-11 rounded-lg border border-border bg-background hover:bg-muted transition-colors duration-150 text-sm font-medium text-foreground no-underline'
          >
            <GoogleIcon />
            Continue with Google
          </a>

          {/* Divider */}
          <div className='relative my-5'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-border' />
            </div>
            <div className='relative flex justify-center'>
              <span className='bg-background px-3 text-xs text-muted-foreground uppercase tracking-wide'>
                or continue with email
              </span>
            </div>
          </div>

          {/* Email / password form */}
          <form onSubmit={handleSubmit} className='space-y-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='email' className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                Email
              </Label>
              <div className='relative'>
                <Mail className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                <Input
                  id='email'
                  type='email'
                  placeholder='you@company.com'
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className='h-10 pl-9'
                  required
                />
              </div>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='pass' className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                Password
              </Label>
              <div className='relative'>
                <Input
                  id='pass'
                  type={showPass ? 'text' : 'password'}
                  placeholder={isSignup ? 'Min. 8 characters' : '••••••••'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className='h-10 pr-10'
                  required
                  minLength={isSignup ? 8 : undefined}
                />
                <button
                  type='button'
                  onClick={() => setShowPass(!showPass)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150'
                >
                  {showPass ? <EyeOff className='w-4 h-4' /> : <Eye className='w-4 h-4' />}
                </button>
              </div>
            </div>

            <Button type='submit' className='w-full h-11 shadow-primary' disabled={loading}>
              {loading ? 'Please wait...' : isSignup ? 'Create Account' : 'Sign In'}
            </Button>

            {/* reCAPTCHA v3 disclosure — required by Google Terms of Service */}
            <p className='text-center text-[11px] text-muted-foreground leading-relaxed'>
              Protected by reCAPTCHA &mdash;{' '}
              <a href='https://policies.google.com/privacy' target='_blank' rel='noopener noreferrer' className='underline hover:text-foreground'>Privacy</a>
              {' '}&amp;{' '}
              <a href='https://policies.google.com/terms' target='_blank' rel='noopener noreferrer' className='underline hover:text-foreground'>Terms</a>
            </p>
          </form>

          {/* Toggle mode */}
          <p className='text-center text-sm text-muted-foreground mt-5'>
            {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type='button'
              onClick={() => onModeChange(isSignup ? 'signin' : 'signup')}
              className='font-semibold text-primary hover:text-primary-light transition-colors duration-150'
            >
              {isSignup ? 'Sign in' : 'Create one free'}
            </button>
          </p>
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
